import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router"
import AppLayout from "../layouts/AppLayout.vue"
import { canAccessRoute } from "../services/auth"
import { useGlobalStore } from "@/stores/global-store"
import ConfigService from "@shared/helpers/configServices"

const baseRoutes: Array<RouteRecordRaw> = [
  {
    path: "/",
    component: AppLayout,
    redirect: { name: "home" },
    children: [
      {
        name: "home",
        path: "",
        meta: { displayName: "dashboard.menuName" },
        component: () => import("../pages/dashboard/Dashboard.vue"),
      },
      {
        name: "settings",
        path: "preferences",
        meta: { displayName: "settings.menuName" },
        component: () => import("../pages/common/Settings.vue"),
      },
      {
        name: "user-info",
        path: "user-info",
        meta: { displayName: "userInfo.menuName" },
        component: () => import("../pages/common/UserInfo.vue"),
      },
      {
        name: "profile",
        path: "profile",
        meta: { displayName: "profile.menuName" },
        component: () => import("../pages/common/Profile.vue"),
      },
      {
        name: "release-notes",
        path: "release-notes",
        meta: { displayName: "releaseNotes.title" },
        component: () => import("../pages/common/ReleaseNotes.vue"),
      },
      {
        name: "help-display",
        path: "help/view/:uniqueId",
        meta: { displayName: "help.menuTitle" },
        component: () => import("../pages/common/HelpDisplayPage.vue"),
      },
    ],
  },
  {
    path: "/admin",
    name: "admin",
    component: AppLayout,
    redirect: { name: "menu-list" },
    children: [
      {
        name: "menu-list",
        path: "menuHierarchy",
        component: () => import("../pages/admin/menuHierarchy/MenuHierarchyListPage.vue"),
      },
      {
        name: "menu-edit",
        path: "menuHierarchy/edit/:id?",
        meta: { parentRoute: "menu-list" },
        component: () => import("../pages/admin/menuHierarchy/MenuHierarchyEditPage.vue"),
      },
      {
        name: "app-customizations",
        path: "app-customizations",
        component: () => import("../pages/admin/appCustomizations/AppCustomizationsPage.vue"),
      },
      {
        name: "help-list",
        path: "help",
        component: () => import("../pages/admin/help/HelpListPage.vue"),
      },
      {
        name: "help-edit",
        path: "help/edit/:uniqueId?",
        meta: { parentRoute: "help-list" },
        component: () => import("../pages/admin/help/HelpEditPage.vue"),
      },
      {
        name: "notification-template-list",
        path: "notificationTemplate",
        component: () => import("../pages/admin/notificationTemplate/NotificationTemplateListPage.vue"),
      },
      {
        name: "notification-template-edit",
        path: "notificationTemplate/edit/:uniqueId?",
        meta: { parentRoute: "notification-template-list" },
        component: () => import("../pages/admin/notificationTemplate/NotificationTemplateEditPage.vue"),
      },
      {
        name: "department-list",
        path: "department",
        component: () => import("../pages/admin/department/DepartmentListPage.vue"),
      },
      {
        name: "businessArea-list",
        path: "businessArea",
        component: () => import("../pages/admin/businessArea/BusinessAreaListPage.vue"),
      },
      {
        name: "businessUnit-list",
        path: "businessUnit",
        component: () => import("../pages/admin/businessUnit/BusinessUnitListPage.vue"),
      },
      {
        name: "division-list",
        path: "division",
        component: () => import("../pages/admin/division/DivisionListPage.vue"),
      },
      {
        name: "organization-list",
        path: "organization",
        component: () => import("../pages/admin/organization/OrganizationListPage.vue"),
      },
      {
        name: "plant-list",
        path: "plant",
        component: () => import("../pages/admin/plant/PlantListPage.vue"),
      },
      {
        name: "personnelArea-list",
        path: "personnelArea",
        component: () => import("../pages/admin/personnelArea/PersonnelAreaListPage.vue"),
      },
      {
        name: "configuration-list",
        path: "configuration",
        component: () => import("../pages/admin/configuration/ConfigurationListPage.vue"),
      },
      {
        name: "widget-list",
        path: "widget",
        component: () => import("../pages/admin/widget/WidgetListPage.vue"),
      },
      {
        name: "widget-edit",
        path: "widget/edit/:uniqueId?",
        meta: { parentRoute: "widget-list" },
        component: () => import("../pages/admin/widget/WidgetEditPage.vue"),
      },
      {
        name: "widget-category-list",
        path: "widgetCategory",
        component: () => import("../pages/admin/widgetCategory/WidgetCategoryListPage.vue"),
      },
      {
        name: "position-list",
        path: "position",
        component: () => import("../pages/admin/position/PositionListPage.vue"),
      },
      {
        name: "position-edit",
        path: "position/edit/:id?",
        meta: { parentRoute: "position-list" },
        component: () => import("../pages/admin/position/PositionEditPage.vue"),
      },
      {
        name: "people-list",
        path: "people",
        component: () => import("../pages/admin/people/PeopleListPage.vue"),
      },
      {
        name: "people-edit",
        path: "people/edit/:uniqueId?",
        meta: { parentRoute: "people-list" },
        component: () => import("../pages/admin/people/PeopleEditPage.vue"),
      },
    ],
  },
  {
    path: "/security",
    name: "security",
    component: AppLayout,
    redirect: { name: "resource-list" },
    children: [
      {
        name: "resource-list",
        path: "resource",
        component: () => import("../pages/security/resource/ResourceListPage.vue"),
      },
      {
        name: "user-list",
        path: "user",
        component: () => import("../pages/security/user/UserListPage.vue"),
      },
      {
        name: "user-edit",
        path: "user/edit/:uniqueId?",
        meta: { parentRoute: "user-list" },
        component: () => import("../pages/security/user/UserEditPage.vue"),
      },
      {
        name: "group-list",
        path: "group",
        component: () => import("../pages/security/group/GroupListPage.vue"),
      },
      {
        name: "group-edit",
        path: "group/edit/:id?",
        meta: { parentRoute: "group-list" },
        component: () => import("../pages/security/group/GroupEditPage.vue"),
      },
    ],
  },
  {
    path: "/communication",
    name: "communication",
    component: AppLayout,
    redirect: { name: "communication-inbox" },
    children: [
      {
        name: "communication-inbox",
        path: "",
        meta: { displayName: "communication.menuName" },
        component: () => import("../pages/communication/CommunicationInboxPage.vue"),
      },
      {
        name: "communication-read",
        path: ":uniqueId",
        meta: { parentRoute: "communication-inbox" },
        component: () => import("../pages/communication/CommunicationReadPage.vue"),
      },
    ],
  },
  {
    path: "/threeHundredSixty",
    name: "three-hundred-sixty",
    component: AppLayout,
    redirect: { name: "question-option-list" },
    children: [
      {
        path: "questionOption",
        name: "question-option-list",
        component: () => import("../pages/threeHundredSixty/questionOption/QuestionOptionListPage.vue"),
      },
      {
        path: "formEmailSender",
        name: "form-email-sender",
        component: () => import("../pages/threeHundredSixty/formEmailSender/FormEmailSenderPage.vue"),
      },
      {
        name: "question-option-edit",
        path: "questionOption/edit/:uniqueId?",
        meta: { parentRoute: "question-option-list" },
        component: () => import("../pages/threeHundredSixty/questionOption/QuestionOptionEditPage.vue"),
      },
      {
        path: "questionGroup",
        name: "question-group-list",
        component: () => import("../pages/threeHundredSixty/questionGroup/QuestionGroupListPage.vue"),
      },
      {
        path: "question",
        name: "question-list",
        component: () => import("../pages/threeHundredSixty/question/QuestionListPage.vue"),
      },
      {
        name: "question-edit",
        path: "question/edit/:uniqueId?",
        meta: { parentRoute: "question-list" },
        component: () => import("../pages/threeHundredSixty/question/QuestionEditPage.vue"),
      },
      {
        path: "formCategory",
        name: "form-category-list",
        component: () => import("../pages/threeHundredSixty/formCategory/FormCategoryListPage.vue"),
      },
      {
        path: "formDelegate",
        name: "form-delegate-list",
        component: () => import("../pages/threeHundredSixty/formDelegate/FormDelegateListPage.vue"),
      },
      {
        path: "formDelegate/:instance",
        name: "form-delegate-global",
        component: () => import("../pages/threeHundredSixty/formDelegate/FormDelegateListPage.vue"),
      },
      {
        path: "formPeople",
        name: "form-people",
        children: [
          {
            path: "participants",
            name: "form-participants",
            component: () => import("../pages/threeHundredSixty/formPeople/participants/FormPeople.vue"),
          },
          {
            path: "sign",
            name: "form-people-sign",
            component: () => import("../pages/threeHundredSixty/formPeople/sign/Sign.vue"),
          },
          {
            path: "signGlobal",
            name: "form-people-sign-global",
            meta: { global: true },
            component: () => import("../pages/threeHundredSixty/formPeople/sign/Sign.vue"),
          },
          {
            path: "resultViewer",
            name: "result-viewer",
            component: () => import("../pages/threeHundredSixty/formPeople/resultViewer/ResultViewerPage.vue"),
          },
        ],
      },
      {
        path: "form",
        name: "form-list",
        component: () => import("../pages/threeHundredSixty/form/FormListPage.vue"),
      },
      {
        name: "form-edit",
        path: "form/edit/:uniqueId?",
        meta: { parentRoute: "form-list" },
        component: () => import("../pages/threeHundredSixty/form/FormEditPage.vue"),
      },
      {
        path: "questionnaire",
        name: "questionnaire",
        children: [
          {
            path: "complete",
            name: "complete-questionnaires",
            component: () => import("../pages/threeHundredSixty/questionnaire/complete/Complete.vue"),
          },
          {
            path: "freeEvaluationReview",
            name: "free-evaluation-review",
            component: () => import("../pages/threeHundredSixty/questionnaire/freeEvaluation/Review.vue"),
          },
          {
            path: "freeEvaluationReviewGlobal",
            name: "free-evaluation-review-global",
            meta: { global: true },
            component: () => import("../pages/threeHundredSixty/questionnaire/freeEvaluation/Review.vue"),
          },
          {
            path: "createCrossGroupForMyself",
            name: "create-cross-group-for-myself",
            component: () =>
              import("../pages/threeHundredSixty/questionnaire/crossGroup/forMyself/CreateCrossGroupMyselfPage.vue"),
          },
          {
            name: "create-cross-group-for-my-team",
            path: "createCrossGroupForMyTeam",
            component: () =>
              import("../pages/threeHundredSixty/questionnaire/crossGroup/forMyTeam/CreateCrossGroupPage.vue"),
          },
          {
            name: "create-cross-group-for-my-remote-team",
            path: "createCrossGroupForMyRemoteTeam/:instance",
            component: () =>
              import("../pages/threeHundredSixty/questionnaire/crossGroup/forMyTeam/CreateCrossGroupPage.vue"),
          },
        ],
      },
    ],
  },
  {
    path: "/report",
    name: "report",
    component: AppLayout,
    redirect: { name: "report-viewer" },
    children: [
      {
        name: "report-viewer",
        path: "viewer/:id",
        component: () => import("../pages/report/viewer/View.vue"),
      },
      {
        name: "report-maintenance-list",
        path: "maintenance",
        component: () => import("../pages/report/maintenance/ReportListPage.vue"),
      },
      {
        name: "report-maintenance-edit",
        path: "maintenance/edit/:id?",
        meta: { parentRoute: "report-maintenance-list" },
        component: () => import("../pages/report/maintenance/ReportEditPage.vue"),
      },
      {
        name: "report-column-template-list",
        path: "columnTemplate",
        component: () => import("../pages/report/maintenance/ReportColumnTemplateListPage.vue"),
      },
    ],
  },
  {
    path: "/org-chart",
    component: AppLayout,
    children: [
      {
        name: "people-org-chart",
        path: "",
        component: () => import("../pages/orgChart/OrgChartPage.vue"),
      },
    ],
  },
  {
    path: "/talent",
    name: "talent",
    component: AppLayout,
    children: [
      {
        name: "degree-types",
        path: "degreeTypes",
        component: () => import("../pages/talent/degreeType/DegreeTypeListPage.vue"),
      },
    ],
  },
  {
    name: "404",
    path: "/404",
    component: () => import("../pages/404.vue"),
  },
  {
    name: "401",
    path: "/401",
    component: () => import("../pages/401.vue"),
  },
  {
    name: "403",
    path: "/403",
    component: () => import("../pages/403.vue"),
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    redirect: { name: "404" },
  },
]

async function loadRemoteRoutes(): Promise<RouteRecordRaw[]> {
  const remoteRoutes: RouteRecordRaw[] = []
  const remoteApps = ConfigService.Current.remoteApps.split(",").map((app: string) => app.trim())

  try {
    if (remoteApps.includes("jtp-policy")) {
      // Check if the remote module is available
      const remoteModule = await import("jtp-policy/routes")
      const policyRoutes = remoteModule?.default
      if (Array.isArray(policyRoutes)) {
        // Add remote routes as children of the main layout
        policyRoutes.forEach((route) => {
          if (route.children) {
            route.children.forEach((child: any) => {
              remoteRoutes.push({
                ...child,
                // Ensure proper path structure
                path: route.path ? `${route.path.slice(1)}/${child.path}` : child.path,
              })
            })
          } else {
            remoteRoutes.push(route)
          }
        })
      }
    }
  } catch (error: any) {
    console.warn("Failed to load remote routes from jtp-policy:", error.message || error)
    console.info("Continuing without remote routes. Make sure jtp-policy is running on the configured port.")
  }

  return remoteRoutes
}

// Create router factory function
export async function createAppRouter() {
  // Load remote routes first
  const remoteRoutes = await loadRemoteRoutes()
  // Merge base routes with remote routes
  const allRoutes = [...baseRoutes]

  // Add remote routes as children of the main layout
  const mainLayoutRoute = allRoutes.find((route) => route.path === "/")
  if (mainLayoutRoute && mainLayoutRoute.children) {
    mainLayoutRoute.children.push(...remoteRoutes)
  }

  const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
      if (savedPosition) {
        return savedPosition
      }
      if (to.hash) {
        return { el: to.hash, behavior: "smooth" }
      } else {
        window.scrollTo(0, 0)
      }
    },
    routes: allRoutes,
  })

  router.beforeEach((to, from, next) => {
    const globalStore = useGlobalStore()
    const routeConfig = globalStore.menuHierarchy.find((r) => r.route === to.name)
    if (routeConfig) {
      if (!canAccessRoute(routeConfig.route || "")) {
        return next({ name: "401" })
      }
    }

    next()
  })

  return router
}

// For backward compatibility, export a default router (without remote routes)
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    if (to.hash) {
      return { el: to.hash, behavior: "smooth" }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes: baseRoutes,
})

export default router
